import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster, toast } from 'react-hot-toast';
import app, { auth, db } from './firebase/config';
import { unlockAudio, playBeep } from './utils/sound';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, onSnapshot, doc, getDoc } from 'firebase/firestore';
import Login from './components/Login';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import Chat from './components/Chat';
import './App.css';
import { initMessagingForUser, subscribeToForegroundMessages } from './firebase/messaging';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Global disguised notifications for incoming messages
  const seenMessageIdsRef = useRef(new Set());
  const notifUnsubRef = useRef(null);

  useEffect(() => {
    if (!user) {
      if (notifUnsubRef.current) {
        notifUnsubRef.current();
        notifUnsubRef.current = null;
      }
      return;
    }

    // Try to unlock audio on first user interaction after login
    const onInteract = () => unlockAudio();
    window.addEventListener('click', onInteract, { once: true });

    // Try to register for FCM (will silently fail on unsupported browsers)
    const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;
    initMessagingForUser(app, user, VAPID_KEY);
    // Removed FCM foreground message listener to prevent popup notifications

    const startAtMs = Date.now();
    const q = query(
      collection(db, 'messages'),
      where('participants', 'array-contains', user.uid)
    );

    const unsub = onSnapshot(q, async (snapshot) => {
      snapshot.docChanges().forEach(async (change) => {
        if (change.type !== 'added') return;
        const msg = change.doc.data();
        const msgId = change.doc.id;
        if (seenMessageIdsRef.current.has(msgId)) return;
        seenMessageIdsRef.current.add(msgId);
        if (!msg?.timestamp?.toMillis || msg.senderId === user.uid) return;
        if (msg.timestamp.toMillis() < startAtMs) return; // ignore history on first attach

        let sourceName = 'Weather Source';
        try {
          const senderSnap = await getDoc(doc(db, 'users', msg.senderId));
          if (senderSnap.exists() && senderSnap.data().username) {
            sourceName = senderSnap.data().username;
          }
        } catch {}

        // Only play sound, no popup notifications
        playBeep(988, 180, 0.06); // B5 beep
      });
    });

    notifUnsubRef.current = unsub;
    return () => {
      if (notifUnsubRef.current) {
        notifUnsubRef.current();
        notifUnsubRef.current = null;
      }
      window.removeEventListener('click', onInteract);
    };
  }, [user]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }
  
  return (
    <Router>
      <div className="App">
        <Toaster position="bottom-center" />
        <Routes>
          <Route 
            path="/login" 
            element={user ? <Navigate to="/dashboard" /> : <Login />} 
          />
          <Route 
            path="/register" 
            element={user ? <Navigate to="/dashboard" /> : <Register />} 
          />
          <Route 
            path="/dashboard" 
            element={user ? <Dashboard /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/chat/:friendId" 
            element={user ? <Chat /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/" 
            element={<Navigate to={user ? "/dashboard" : "/login"} />} 
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
